import React, { useEffect, useRef, useState } from 'react';

export interface SyncDebugOverlayProps {
  visible: boolean;
  mediaRef: React.RefObject<HTMLMediaElement | null>;
  role: 'source' | 'follower';
  driftMs?: number;
  syncState?: string;
  colId?: string;
}

function derivePlaybackState(el: HTMLMediaElement | null): string {
  if (!el) return 'no-media';
  if (el.seeking) return 'seeking';
  if (el.paused) return 'paused';
  if (el.readyState < 3) return 'waiting';
  return 'playing';
}

export default function SyncDebugOverlay({
  visible,
  mediaRef,
  role,
  driftMs,
  syncState,
  colId,
}: SyncDebugOverlayProps): React.ReactElement | null {
  const [, setTick] = useState(0);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!visible) return;
    const update = () => {
      setTick((t) => t + 1);
      rafRef.current = requestAnimationFrame(update);
    };
    rafRef.current = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafRef.current);
  }, [visible]);

  if (!visible) return null;

  // Read live sync data from the global Map if colId is provided
  let liveDriftMs = driftMs;
  let liveSyncState = syncState;
  if (colId && role === 'follower') {
    const syncData = (window as any).__playerSyncFollowerState?.get(colId);
    if (syncData) {
      liveDriftMs = syncData.driftMs ?? liveDriftMs;
      liveSyncState = syncData.syncState ?? liveSyncState;
    }
  }

  const mediaElement = mediaRef.current;
  const time = mediaElement?.currentTime ?? 0;
  const rate = mediaElement?.playbackRate ?? 1;
  const playback = derivePlaybackState(mediaElement);

  const driftLabel =
    role === 'source'
      ? '\u2014'
      : liveDriftMs != null
        ? `${liveDriftMs >= 0 ? '+' : ''}${Math.round(liveDriftMs)}ms`
        : '?';

  const stateLabel = role === 'source' ? '\u2014' : liveSyncState ?? '?';

  const style: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    background: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    fontFamily: 'monospace',
    fontSize: '11px',
    lineHeight: '18px',
    padding: '2px 8px',
    pointerEvents: 'none' as const,
    display: 'flex',
    gap: '12px',
    whiteSpace: 'nowrap' as const,
  };

  return (
    <div data-testid="sync-debug-overlay" style={style}>
      <span style={{ color: role === 'source' ? '#4fc3f7' : '#aed581' }}>
        {role === 'source' ? 'SOURCE' : 'FOLLOWER'}
      </span>
      <span>T:{time.toFixed(3)}s</span>
      <span>Rate:{rate.toFixed(3)}x</span>
      <span>Drift:{driftLabel}</span>
      <span>Sync:{stateLabel}</span>
      <span>PB:{playback}</span>
    </div>
  );
}
